import { useState, useEffect } from 'react';
import { FiDownload, FiX, FiAlertCircle, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { useLanguageStore } from '../../store/languageStore'
import { useTranslation } from '../../hooks/useTranslation';
import { formatDate } from '../../utils/formatters';
import { apiClient } from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';
import { useQuery } from '@tanstack/react-query';
import { getAppVersion, setLastUpdateCheck, compareVersions } from '../../utils/version';
import { autoUpdateService, UpdateProgress } from '../../services/autoUpdateService';
import UpdateProgressModal from '../updates/UpdateProgressModal';
import toast from 'react-hot-toast';

interface UpdateInfo {
  hasUpdate: boolean;
  latestVersion: {
    id: number;
    versionNumber: string;
    versionName?: string;
    releaseDate: string;
    updateType?: string;
    requiresDatabaseUpdate: boolean;
    requiresAPIUpdate: boolean;
    databaseScripts?: string;
    apiScripts?: string;
    description?: string;
    descriptionEn?: string;
    isMandatory: boolean;
  };
  currentVersion?: string;
  newerVersions?: Array<{
    id: number;
    versionNumber: string;
    versionName?: string;
    releaseDate: string;
    updateType?: string;
    requiresDatabaseUpdate: boolean;
    requiresAPIUpdate: boolean;
    description?: string;
    descriptionEn?: string;
    isMandatory: boolean;
  }>;
  message: string;
}

interface UpdateNotificationProps {
  currentVersion?: string;
  onDismiss?: () => void;
}

export default function UpdateNotification({ currentVersion, onDismiss }: UpdateNotificationProps) {
  const { language } = useLanguageStore();
  const { translate: t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState<UpdateProgress | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const isRTL = language === 'ar';

  // Get current version from localStorage or use provided
  const appVersion = currentVersion || getAppVersion();

  const { data: updateInfo, isLoading } = useQuery<UpdateInfo>({
    queryKey: ['appVersionCheck', appVersion],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.appVersions.check, {
        params: { currentVersion: appVersion },
      });
      return response.data;
    },
    refetchInterval: 300000, // Check every 5 minutes
    retry: 1,
  });

  useEffect(() => {
    const latest = updateInfo?.latestVersion?.versionNumber
    const trulyNewer =
      !!updateInfo?.hasUpdate &&
      !!latest &&
      compareVersions(latest, appVersion) > 0

    if (trulyNewer) {
      setIsVisible(true)
      setLastUpdateCheck()
    } else {
      setIsVisible(false)
      setIsPanelOpen(false)
    }
  }, [updateInfo, appVersion]);

  // Listen to update progress
  useEffect(() => {
    const unsubscribe = autoUpdateService.onProgress((progress) => {
      setUpdateProgress(progress);
      if (progress.stage === 'Complete' || progress.hasError) {
        setIsUpdating(false);
      }
    });

    return unsubscribe;
  }, []);

  const handleDismiss = () => {
    setIsPanelOpen(false);
    onDismiss?.();
  };

  const handleDownloadUpdate = async () => {
    if (!updateInfo?.latestVersion) return;

    try {
      setIsUpdating(true);
      setShowProgressModal(true);

      // Start automatic update process
      const result = await autoUpdateService.installUpdate({
        version: updateInfo.latestVersion.versionNumber,
        createBackup: true,
        backupDatabase: updateInfo.latestVersion.requiresDatabaseUpdate,
        restartService: true,
      });

      if (result.success) {
        toast.success(
          t('updates.updateSuccess').replace('{version}', result.newVersion || '')
        );
      } else {
        toast.error(`${t('updates.updateError')}: ${result.message}`);
      }
    } catch (error: any) {
      toast.error(`${t('updates.updateError')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  const handleDownloadInstructions = () => {
    // Open update instructions or download page
    const updateDetails = {
      version: updateInfo?.latestVersion.versionNumber,
      releaseDate: updateInfo?.latestVersion.releaseDate,
      requiresDatabase: updateInfo?.latestVersion.requiresDatabaseUpdate,
      requiresAPI: updateInfo?.latestVersion.requiresAPIUpdate,
      databaseScripts: updateInfo?.latestVersion.databaseScripts,
      apiScripts: updateInfo?.latestVersion.apiScripts,
      description: updateInfo?.latestVersion.description,
      descriptionEn: updateInfo?.latestVersion.descriptionEn,
    };

    // Store update info for admin
    localStorage.setItem('pendingUpdate', JSON.stringify(updateDetails));
    
    // Create and download update instructions file
    const instructions = `${t('updates.updateInstructionsTitle')} ${updateInfo?.latestVersion.versionNumber}\n` +
        `${t('updates.releaseDateLabel')} ${formatDate(updateInfo?.latestVersion.releaseDate)}\n\n` +
        ((language === 'ar' ? updateInfo?.latestVersion.description : updateInfo?.latestVersion.descriptionEn || updateInfo?.latestVersion.description) || '') + '\n\n' +
        `${t('updates.updateSteps')}\n` +
        (updateInfo?.latestVersion.requiresDatabaseUpdate 
          ? `${t('updates.runDbScripts')}\n   ${updateInfo?.latestVersion.databaseScripts || 'N/A'}\n\n`
          : '') +
        (updateInfo?.latestVersion.requiresAPIUpdate 
          ? `${t('updates.updateApiFiles')}\n   ${updateInfo?.latestVersion.apiScripts || 'N/A'}\n\n`
          : '') +
        `${t('updates.updateFrontend')}\n` +
        `${t('updates.updateLocalStorage')}\n\n` +
        t('updates.pendingUpdateNote');

    // Download as text file
    const blob = new Blob([instructions], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Update_Instructions_${updateInfo?.latestVersion.versionNumber}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (
    !isVisible ||
    !updateInfo?.hasUpdate ||
    !updateInfo.latestVersion ||
    compareVersions(updateInfo.latestVersion.versionNumber, appVersion) <= 0 ||
    isLoading
  ) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsPanelOpen((open) => !open)}
        className={`fixed ${
          isRTL ? 'left-4' : 'right-4'
        } top-20 z-50 flex h-11 w-11 items-center justify-center rounded-full border-2 text-white shadow-xl transition hover:scale-105 ${
          updateInfo.latestVersion.isMandatory
            ? 'border-red-200 bg-red-600 hover:bg-red-700'
            : 'border-violet-200 bg-violet-600 hover:bg-violet-700'
        }`}
        title={t('updates.updateAvailable')}
        aria-label={t('updates.updateAvailable')}
        aria-expanded={isPanelOpen}
      >
        <FiDownload size={20} />
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold text-white">
          1
        </span>
      </button>

      {isPanelOpen && (
        <div
          className={`fixed ${
            isRTL ? 'left-4' : 'right-4'
          } top-32 z-50 max-h-[calc(100vh-9rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-lg border-2 bg-white shadow-2xl ${
            updateInfo.latestVersion.isMandatory
              ? 'border-red-500'
              : 'border-yellow-400'
          }`}
        >
          <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 flex-1">
            {updateInfo.latestVersion.isMandatory ? (
              <FiAlertCircle className="text-red-600 mt-1 flex-shrink-0" size={24} />
            ) : (
              <FiInfo className="text-yellow-600 mt-1 flex-shrink-0" size={24} />
            )}
            <div className="flex-1">
              <h3 className="font-bold text-lg text-gray-800">
                {t('updates.updateAvailable')}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('updates.currentVersion')}: {appVersion}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title={language === 'ar' ? 'إغلاق' : 'Close'}
            aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Update Info */}
        <div className="mb-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-blue-900">
                {t('updates.newVersion')}
              </span>
              <span className="font-bold text-blue-700">
                {updateInfo.latestVersion.versionNumber}
              </span>
            </div>
            {updateInfo.latestVersion.versionName && (
              <p className="text-sm text-blue-800 mb-1">
                {updateInfo.latestVersion.versionName}
              </p>
            )}
            <p className="text-xs text-blue-600">
              {t('updates.releaseDateLabel')}{' '}
              {formatDate(updateInfo.latestVersion.releaseDate)}
            </p>
          </div>

          {/* Update Requirements */}
          {(updateInfo.latestVersion.requiresDatabaseUpdate ||
            updateInfo.latestVersion.requiresAPIUpdate) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-2">
              <p className="text-xs font-semibold text-yellow-800 mb-1">
                {t('updates.updateRequirements')}
              </p>
              <ul className={`text-xs text-yellow-700 ${isRTL ? 'mr-4' : 'ml-4'} list-disc`}>
                {updateInfo.latestVersion.requiresDatabaseUpdate && (
                  <li>
                    {t('updates.dbUpdateRequired')}
                  </li>
                )}
                {updateInfo.latestVersion.requiresAPIUpdate && (
                  <li>
                    {t('updates.apiUpdateRequired')}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Description */}
          {updateInfo.latestVersion.description && (
            <div className="text-sm text-gray-700 mb-2">
              {language === 'ar'
                ? updateInfo.latestVersion.description
                : updateInfo.latestVersion.descriptionEn || updateInfo.latestVersion.description}
            </div>
          )}

          {/* Expandable Details */}
          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              {updateInfo.latestVersion.databaseScripts && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    {t('updates.dbScripts')}
                  </p>
                  <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 font-mono">
                    {(() => {
                      try {
                        const scripts = JSON.parse(updateInfo.latestVersion.databaseScripts);
                        return Array.isArray(scripts) 
                          ? scripts.join('\n')
                          : updateInfo.latestVersion.databaseScripts;
                      } catch {
                        return updateInfo.latestVersion.databaseScripts;
                      }
                    })()}
                  </div>
                </div>
              )}
              {updateInfo.latestVersion.apiScripts && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    {t('updates.apiScripts')}
                  </p>
                  <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 font-mono">
                    {(() => {
                      try {
                        const scripts = JSON.parse(updateInfo.latestVersion.apiScripts);
                        return Array.isArray(scripts) 
                          ? scripts.join('\n')
                          : updateInfo.latestVersion.apiScripts;
                      } catch {
                        return updateInfo.latestVersion.apiScripts;
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
          <button
            onClick={handleDownloadUpdate}
            disabled={isUpdating}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              updateInfo.latestVersion.isMandatory
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {isUpdating ? (
              <>
                <FiRefreshCw className="animate-spin" />
                <span>{t('updates.updating')}</span>
              </>
            ) : (
              <>
                <FiDownload />
                <span>
                  {t('updates.updateNow')}
                </span>
              </>
            )}
          </button>
          <button
            onClick={handleDownloadInstructions}
            disabled={isUpdating}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm disabled:opacity-50"
            title={t('updates.downloadInstructions')}
          >
            <FiDownload className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            disabled={isUpdating}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm disabled:opacity-50"
          >
            {isExpanded
              ? t('updates.hideDetails')
              : t('updates.showDetails')}
          </button>
        </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      <UpdateProgressModal
        isOpen={showProgressModal}
        progress={updateProgress}
        onClose={() => {
          if (updateProgress?.isComplete || updateProgress?.hasError) {
            setShowProgressModal(false);
          }
        }}
      />
    </>
  );
}


